import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader, useToast } from 'components/admin';
import { Button, Checkbox, Select } from 'components/common';
import RichTextEditor from 'components/admin/RichTextEditor';
import ProductSolutionSectionsEditor, { createNewSection } from '../shared/ProductSolutionSectionsEditor';
import { createProductSolutionContent, getAdminProductSolutionContentById, updateProductSolutionContent } from 'apis/adminProductSolutionContents';
import { getAdminProductSolutionCategories } from 'apis/adminProductSolutionCategories';
import { useActiveLanguagesQuery, useAdminCreateMutation, useAdminDetailQuery, useAdminListQuery, useAdminUpdateMutation } from 'hooks';
import { FiLayers, FiEdit3 } from 'react-icons/fi';
import '../../../styles/admin-shared.scss';

const ProductSolutionContentsForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [activeTab, setActiveTab] = useState(0);
    const [editorMode, setEditorMode] = useState('structured'); // 'structured' | 'quill'
    const [formData, setFormData] = useState({
        productSolutionCategoryId: '',
        isActive: true,
        translation: [],
        sections: [],
        images: [],
    });

    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: categoriesData = { items: [] } } = useAdminListQuery(
        'product-solution-categories-options',
        { pageSize: 1000, languageCode: 'vi' },
        getAdminProductSolutionCategories
    );
    const categories = categoriesData.items;
    const { data: detail, error: detailError } = useAdminDetailQuery(
        'product-solution-contents',
        id,
        getAdminProductSolutionContentById
    );
    const createMutation = useAdminCreateMutation('product-solution-contents', createProductSolutionContent);
    const updateMutation = useAdminUpdateMutation('product-solution-contents', updateProductSolutionContent);
    const loading = createMutation.isPending || updateMutation.isPending;

    useEffect(() => {
        if (!id && languages.length) {
            setFormData(prev => ({
                ...prev,
                translation: languages.map(l => ({ languageId: l.id, description: '' })),
                sections: prev.sections.length ? prev.sections : [createNewSection(languages, 0)],
            }));
        }
    }, [id, languages]);

    useEffect(() => {
        if (detail && languages.length) {
            const mergedTranslation = languages.map(lang => {
                const existing = (detail.translation || []).find(t => Number(t.languageId) === Number(lang.id));
                return existing || { languageId: lang.id, description: '' };
            });

            const mergedSections = (detail.sections || []).map(sec => ({
                ...sec,
                translation: languages.map(lang => {
                    const existing = (sec.translation || []).find(t => Number(t.languageId) === Number(lang.id));
                    return existing || { languageId: lang.id, title: '', subtitle: '', badge: '', description: '' };
                }),
                items: (sec.items || []).map(it => ({
                    ...it,
                    translation: languages.map(lang => {
                        const existing = (it.translation || []).find(t => Number(t.languageId) === Number(lang.id));
                        return existing || { languageId: lang.id, title: '', content: '', caption: '', badge: '' };
                    }),
                })),
            }));

            // Determine initial editor mode
            if (mergedSections.length > 0) {
                setEditorMode('structured');
            } else if (mergedTranslation.some(t => t.description && t.description.trim())) {
                setEditorMode('quill');
            }

            setFormData({
                ...detail,
                productSolutionCategoryId: detail.productSolutionCategoryId || '',
                translation: mergedTranslation,
                sections: mergedSections.length > 0 ? mergedSections : [createNewSection(languages, 0)],
            });
        }
    }, [detail, languages]);

    useEffect(() => {
        if (detailError) toast.error(detailError.message || 'Tải dữ liệu thất bại');
    }, [detailError, toast]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleTranslationChange = (langId, field, value) => {
        setFormData(prev => ({
            ...prev,
            translation: prev.translation.map(t =>
                Number(t.languageId) === Number(langId) ? { ...t, [field]: value } : t
            ),
        }));
    };

    const handleSectionsChange = (newSections) => {
        setFormData(prev => ({ ...prev, sections: newSections }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                productSolutionCategoryId: formData.productSolutionCategoryId ? Number(formData.productSolutionCategoryId) : null,
                isActive: Boolean(formData.isActive),
                translation: languages.map(lang => {
                    const t = formData.translation.find(x => Number(x.languageId) === Number(lang.id)) || {};
                    return {
                        languageId: lang.id,
                        description: t.description || '',
                    };
                }),
                sections: editorMode === 'structured' ? formData.sections.map((sec, sIdx) => ({
                    id: sec.id,
                    sortOrder: sec.sortOrder !== undefined ? Number(sec.sortOrder) : sIdx,
                    isActive: sec.isActive !== undefined ? Boolean(sec.isActive) : true,
                    translation: languages.map(lang => {
                        const st = (sec.translation || []).find(x => Number(x.languageId) === Number(lang.id)) || {};
                        return {
                            languageId: lang.id,
                            title: st.title || '',
                            subtitle: st.subtitle || '',
                            badge: st.badge || '',
                            description: st.description || '',
                        };
                    }),
                    items: (sec.items || []).map((it, iIdx) => ({
                        id: it.id,
                        itemType: it.itemType || 'text',
                        layout: it.layout || 'left',
                        imageUrl: it.imageUrl || null,
                        sortOrder: it.sortOrder !== undefined ? Number(it.sortOrder) : iIdx,
                        isActive: it.isActive !== undefined ? Boolean(it.isActive) : true,
                        translation: languages.map(lang => {
                            const itTrans = (it.translation || []).find(x => Number(x.languageId) === Number(lang.id)) || {};
                            return {
                                languageId: lang.id,
                                title: itTrans.title || '',
                                content: itTrans.content || '',
                                caption: itTrans.caption || '',
                                badge: itTrans.badge || '',
                            };
                        }),
                    })),
                })) : [],
                images: formData.images?.filter(img => img.imageUrl).map(img => ({
                    id: img.id,
                    imageUrl: img.imageUrl,
                    sortOrder: Number(img.sortOrder) || 0,
                })) || [],
            };

            const res = id
                ? await updateMutation.mutateAsync({ id, data: payload })
                : await createMutation.mutateAsync(payload);

            if (res.success) {
                toast.success(id ? 'Cập nhật thành công' : 'Thêm mới thành công');
                navigate('/admin/product-solution-contents');
            } else {
                toast.error(res.message || 'Lưu thất bại');
            }
        } catch (err) {
            console.error('Submit error:', err);
            toast.error('Lưu thất bại');
        }
    };

    return (
        <div className="admin-page">
            <PageHeader title={id ? 'Cập nhật Nội dung Sản phẩm / Giải pháp' : 'Thêm Nội dung Sản phẩm / Giải pháp'} />
            <form className="form-card ps-content-admin-form" onSubmit={handleSubmit}>
                <p className="form-section-title">Cài đặt chung</p>
                <div className="form-row">
                    <Select
                        label="Danh mục Sản phẩm / Giải pháp *"
                        name="productSolutionCategoryId"
                        value={formData.productSolutionCategoryId}
                        onChange={handleChange}
                        required
                        options={[
                            { value: '', label: '— Chọn danh mục —' },
                            ...categories.map(c => ({ value: c.id, label: c.name || `Danh mục #${c.id}` })),
                        ]}
                    />
                </div>
                <Checkbox label="Hoạt động" name="isActive" checked={formData.isActive} onChange={handleChange} />

                {/* Editor Mode Selector */}
                <div className="ps-mode-selector-card">
                    <div className="ps-mode-selector-label">Chọn chế độ soạn thảo nội dung:</div>
                    <div className="ps-mode-options">
                        <button
                            type="button"
                            className={`ps-mode-btn ${editorMode === 'structured' ? 'active' : ''}`}
                            onClick={() => setEditorMode('structured')}
                        >
                            <span className="ps-mode-icon"><FiLayers size={18} /></span>
                            <div className="ps-mode-info">
                                <strong>Khối &amp; Trường thông tin (Khuyên dùng)</strong>
                                <span>Phân chia theo phân đoạn (Sections) và các trường Text, Ảnh, Text+Ảnh, Thẻ nổi bật</span>
                            </div>
                        </button>

                        <button
                            type="button"
                            className={`ps-mode-btn ${editorMode === 'quill' ? 'active' : ''}`}
                            onClick={() => setEditorMode('quill')}
                        >
                            <span className="ps-mode-icon"><FiEdit3 size={18} /></span>
                            <div className="ps-mode-info">
                                <strong>Văn bản tự do (Quill Rich Text)</strong>
                                <span>Soạn thảo văn bản HTML tự do theo kiểu bài viết truyền thống</span>
                            </div>
                        </button>
                    </div>
                </div>

                {languages.length > 0 && (
                    <>
                        <div className="ps-editor-language-tabs-wrapper">
                            <p className="form-section-title">Nội dung theo ngôn ngữ</p>
                            <div className="translation-tabs">
                                <div className="tab-header">
                                    {languages.map((lang, i) => (
                                        <button
                                            key={lang.id}
                                            type="button"
                                            className={`tab-btn ${activeTab === i ? 'active' : ''}`}
                                            onClick={() => setActiveTab(i)}
                                        >
                                            {lang.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* MODE 1: Structured Sections & Items Editor */}
                        {editorMode === 'structured' && (
                            <ProductSolutionSectionsEditor
                                sections={formData.sections || []}
                                languages={languages}
                                activeTab={activeTab}
                                onChange={handleSectionsChange}
                            />
                        )}

                        {/* MODE 2: Standard Quill Rich Text Editor */}
                        {editorMode === 'quill' && (
                            <div className="ps-quill-editor-wrapper">
                                {languages.map((lang, i) => {
                                    const t = formData.translation.find(x => Number(x.languageId) === Number(lang.id)) || {};
                                    return activeTab === i ? (
                                        <div key={lang.id} className="tab-content" style={{ marginTop: 12 }}>
                                            <div className="admin-form-group">
                                                <label>Mô tả chi tiết ({lang.name})</label>
                                                <RichTextEditor
                                                    value={t.description || ''}
                                                    onChange={(content) => handleTranslationChange(lang.id, 'description', content)}
                                                    placeholder={`Nhập mô tả sản phẩm (${lang.name})...`}
                                                />
                                            </div>
                                        </div>
                                    ) : null;
                                })}
                            </div>
                        )}
                    </>
                )}

                <div className="form-actions" style={{ marginTop: 32 }}>
                    <Button type="submit" disabled={loading}>{loading ? 'Đang lưu...' : id ? 'Cập nhật' : 'Thêm mới'}</Button>
                    <Button type="button" variant="secondary" onClick={() => navigate('/admin/product-solution-contents')}>Hủy</Button>
                </div>
            </form>
        </div>
    );
};

export default ProductSolutionContentsForm;
